// EM²c procedural building meshes — hex-native modular renderer.
//
// HARD RULES ENFORCED HERE:
// - Gameplay footprintHexes are the visual footprint. No stretched rectangle may
//   visually occupy cells that the placement system does not own.
// - Horizontal module size is fixed. Large buildings grow by adding footprint
//   modules instead of scaling one mesh larger.
// - Doors/details keep fixed human scale.
// - Visual rotation uses the same 60° direction as rotateFootprint().
// - Neighboring modules belonging to ONE building receive visual-only infill so
//   the structure reads as a continuous colored mass without changing occupancy.

import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { HEX_SIZE, axialToWorld } from "../hexgrid";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { getColonyCategoryColors } from "../data/colonyCategoryColors";
import OneHexCommandHub from "./OneHexCommandHub";
import { BUILDINGS as KIT_BUILDINGS, createBuilding as createKitBuilding, updateBuilding as updateKitBuilding, bakeBuilding as bakeKitBuilding } from "./EM2CBuildingKit";

const KIT_BY_NAME = new Map(KIT_BUILDINGS.map(item => [item.name, item.id]));

const HEX_THETA_START = Math.PI / 2; // vertices align with flat-top gameplay hexes
const FOUNDATION_RADIUS = HEX_SIZE * 0.995;
const FOUNDATION_INSET = HEX_SIZE * 0.965;
const MODULE_W = 3.5;
const MODULE_D = 3.5;
const DOOR_W = 1.0;
const DOOR_H = 2.2;

// Only three directions are needed so every shared edge is emitted once.
const FORWARD_HEX_DIRS = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
];

function MatBody({ color, emissive, emissiveIntensity = 0, opacity = 1, transparent = false }) {
  return (
    <meshStandardMaterial
      color={color}
      emissive={emissive || "#000000"}
      emissiveIntensity={emissiveIntensity}
      roughness={0.6}
      metalness={0.35}
      transparent={transparent}
      opacity={opacity}
    />
  );
}

function HexPrism({ radius, height, y, color, emissive, emissiveIntensity = 0, opacity = 1, transparent = false, castShadow = false, receiveShadow = true }) {
  return (
    <mesh position={[0, y, 0]} castShadow={castShadow} receiveShadow={receiveShadow}>
      <cylinderGeometry args={[radius, radius, height, 6, 1, false, HEX_THETA_START]} />
      <MatBody
        color={color}
        emissive={emissive}
        emissiveIntensity={emissiveIntensity}
        transparent={transparent}
        opacity={opacity}
      />
    </mesh>
  );
}

function HexFoundationCell({ body, accent, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  return (
    <group>
      {/* Almost full-cell dark structural rim. */}
      <HexPrism radius={FOUNDATION_RADIUS} height={0.18} y={0.09} color={dark} opacity={op} transparent={under} />
      {/* Body-colour plate now reaches almost to the grid edge, removing red ground gaps. */}
      <HexPrism radius={FOUNDATION_INSET} height={0.12} y={0.22} color={body} opacity={op} transparent={under} />
      {/* Accent stays as a small inset trim rather than becoming the floor colour. */}
      <HexPrism
        radius={FOUNDATION_INSET * 0.88}
        height={0.025}
        y={0.295}
        color={accent}
        emissive={accent}
        emissiveIntensity={0.18}
        opacity={under ? op * 0.65 : 0.20}
        transparent
        receiveShadow={false}
      />
    </group>
  );
}

function FootprintFoundations({ footprint, body, accent, dark, under, progress }) {
  return (
    <group>
      {footprint.map((cell, index) => {
        const p = axialToWorld(cell.q, cell.r);
        return (
          <group key={`${cell.q},${cell.r},${index}`} position={[p.x, 0, p.z]}>
            <HexFoundationCell body={body} accent={accent} dark={dark} under={under} progress={progress} />
          </group>
        );
      })}
    </group>
  );
}

// Visual-only weld plates between adjacent footprint cells. These never become
// occupied cells and are not read by placement/pathing/economy code. They only
// cover the narrow triangular/diamond ground cracks visible between modules.
function FootprintSeamFill({ footprint, body, under, progress }) {
  const occupied = new Set(footprint.map((cell) => `${cell.q},${cell.r}`));
  const op = under ? 0.5 + progress * 0.5 : 1;
  const seams = [];

  for (const cell of footprint) {
    const a = axialToWorld(cell.q, cell.r);
    for (const dir of FORWARD_HEX_DIRS) {
      const nq = cell.q + dir.q;
      const nr = cell.r + dir.r;
      if (!occupied.has(`${nq},${nr}`)) continue;

      const b = axialToWorld(nq, nr);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const distance = Math.hypot(dx, dz);
      seams.push({
        key: `${cell.q},${cell.r}-${nq},${nr}`,
        x: (a.x + b.x) / 2,
        z: (a.z + b.z) / 2,
        distance,
        rotationY: -Math.atan2(dz, dx),
      });
    }
  }

  return (
    <group>
      {seams.map((seam) => (
        <mesh
          key={seam.key}
          position={[seam.x, 0.245, seam.z]}
          rotation={[0, seam.rotationY, 0]}
          receiveShadow
        >
          <boxGeometry args={[seam.distance * 0.46, 0.10, HEX_SIZE * 0.90]} />
          <MatBody color={body} transparent={under} opacity={op} />
        </mesh>
      ))}
    </group>
  );
}

function AnnexModule({ h, shape, body, accent, panel, dark, under, progress, animRef }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  const annexH = Math.max(0.7, Math.min(2.0, h * 0.58));

  if (shape === "flat") {
    return (
      <group>
        <mesh position={[0, 0.48, 0]}>
          <cylinderGeometry args={[0.34, 0.42, 0.42, 6, 1, false, HEX_THETA_START]} />
          <MatBody color={dark} transparent={tr} opacity={op} />
        </mesh>
        <mesh position={[0, 0.78, 0]} castShadow receiveShadow>
          <boxGeometry args={[MODULE_W, 0.10, MODULE_D]} />
          <MatBody color={body} transparent={tr} opacity={op} />
        </mesh>
        <mesh position={[0, 0.845, 0]}>
          <boxGeometry args={[MODULE_W * 0.88, 0.02, MODULE_D * 0.88]} />
          <MatBody color={accent} emissive={accent} emissiveIntensity={0.35} transparent={tr} opacity={op} />
        </mesh>
      </group>
    );
  }

  if (shape === "open") {
    const postR = 1.35;
    return (
      <group>
        {[0, 2, 4].map((edge) => {
          const a = edge * Math.PI / 3;
          return (
            <mesh key={edge} position={[Math.cos(a) * postR, annexH / 2 + 0.28, Math.sin(a) * postR]} castShadow>
              <boxGeometry args={[0.22, annexH, 0.22]} />
              <MatBody color={body} transparent={tr} opacity={op} />
            </mesh>
          );
        })}
        <HexPrism radius={1.55} height={0.12} y={annexH + 0.28} color={accent} emissive={accent} emissiveIntensity={0.25} opacity={op} transparent={tr} />
      </group>
    );
  }

  if (shape === "sealed" || shape === "cylindrical") {
    return (
      <group>
        <mesh position={[0, annexH / 2 + 0.28, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[1.48, 1.48, annexH, 12]} />
          <MatBody color={body} transparent={tr} opacity={op} />
        </mesh>
        <mesh position={[0, annexH + 0.28, 0]}>
          <cylinderGeometry args={[1.36, 1.48, 0.18, 12]} />
          <MatBody color={panel} transparent={tr} opacity={op} />
        </mesh>
        <mesh position={[0, annexH * 0.62 + 0.28, 1.50]}>
          <boxGeometry args={[0.78, 0.24, 0.04]} />
          <MatBody color={accent} emissive={accent} emissiveIntensity={0.35} transparent={tr} opacity={op} />
        </mesh>
      </group>
    );
  }

  return (
    <group>
      <HexPrism radius={1.65} height={annexH} y={annexH / 2 + 0.28} color={body} opacity={op} transparent={tr} castShadow />
      <HexPrism radius={1.48} height={0.12} y={annexH + 0.34} color={dark} opacity={op} transparent={tr} />
      <mesh position={[0, annexH * 0.62 + 0.28, 1.66]}>
        <boxGeometry args={[0.78, 0.20, 0.04]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.30} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function FootprintAnnexes({ footprint, h, shape, body, accent, panel, dark, under, progress, animRef }) {
  return (
    <group>
      {footprint.map((cell, index) => {
        if (cell.q === 0 && cell.r === 0) return null;
        const p = axialToWorld(cell.q, cell.r);
        return (
          <group key={`annex-${cell.q},${cell.r},${index}`} position={[p.x, 0, p.z]}>
            <AnnexModule
              h={h}
              shape={shape}
              body={body}
              accent={accent}
              panel={panel}
              dark={dark}
              under={under}
              progress={progress}
              animRef={animRef}
            />
          </group>
        );
      })}
    </group>
  );
}

function BoxShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  return (
    <group>
      <mesh position={[0, h / 2 + 0.28, 0]} castShadow receiveShadow>
        <boxGeometry args={[MODULE_W, h, MODULE_D]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, h + 0.38, 0]}>
        <boxGeometry args={[MODULE_W * 0.78, 0.18, MODULE_D * 0.78]} />
        <MatBody color={dark} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, h * 0.62 + 0.28, MODULE_D / 2 + 0.02]}>
        <boxGeometry args={[MODULE_W * 0.76, 0.22, 0.04]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.30} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, DOOR_H / 2 + 0.28, MODULE_D / 2 + 0.04]}>
        <boxGeometry args={[DOOR_W, DOOR_H, 0.06]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function SealedShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  const radius = 1.72;
  return (
    <group>
      <mesh position={[0, h / 2 + 0.28, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[radius, radius, h, 12]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, h + 0.28, 0]}>
        <sphereGeometry args={[radius, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, h * 0.62 + 0.28, radius + 0.04]}>
        <boxGeometry args={[1.15, 0.34, 0.05]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.40} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, DOOR_H / 2 + 0.28, radius + 0.05]}>
        <boxGeometry args={[DOOR_W, DOOR_H, 0.06]} />
        <MatBody color={dark} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function HeavyShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  return (
    <group>
      <HexPrism radius={1.78} height={h} y={h / 2 + 0.28} color={body} opacity={op} transparent={tr} castShadow />
      <HexPrism radius={1.94} height={0.32} y={0.44} color={dark} opacity={op} transparent={tr} />
      <mesh position={[0, h * 0.56 + 0.28, 0]}>
        <cylinderGeometry args={[0.55, 0.55, h * 0.58, 12]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.55} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, DOOR_H / 2 + 0.28, 1.80]}>
        <boxGeometry args={[DOOR_W, DOOR_H, 0.08]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function MechanicalShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  return (
    <group>
      <mesh position={[0, h * 0.32 + 0.28, 0]} castShadow receiveShadow>
        <boxGeometry args={[3.05, h * 0.64, 3.05]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      {[[1.30, 1.30], [-1.30, 1.30], [1.30, -1.30], [-1.30, -1.30]].map(([x, z], i) => (
        <mesh key={i} position={[x, h * 0.36 + 0.28, z]}>
          <cylinderGeometry args={[0.11, 0.14, h * 0.72, 8]} />
          <MatBody color={dark} transparent={tr} opacity={op} />
        </mesh>
      ))}
      <mesh position={[0, h * 0.72 + 0.28, 0]}>
        <cylinderGeometry args={[0.52, 0.52, 0.34, 12]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.40} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, DOOR_H / 2 + 0.28, 1.55]}>
        <boxGeometry args={[DOOR_W, DOOR_H, 0.06]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function SlimShape({ h, body, accent, panel, dark, under, progress, animRef }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  return (
    <group>
      <mesh position={[0, h / 2 + 0.28, 0]} castShadow>
        <cylinderGeometry args={[0.30, 0.42, h, 8]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      <mesh ref={animRef} position={[0, h + 0.28, 0]}>
        <sphereGeometry args={[0.50, 12, 12]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.50} transparent={tr} opacity={op} />
      </mesh>
      <HexPrism radius={0.92} height={0.28} y={0.42} color={dark} opacity={op} transparent={tr} />
      <mesh position={[0, h + 1.03, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 1.5, 4]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function CleanShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  return (
    <group>
      <HexPrism radius={1.72} height={h} y={h / 2 + 0.28} color={panel} opacity={op} transparent={tr} castShadow />
      <HexPrism radius={1.82} height={0.16} y={h + 0.36} color={dark} opacity={op} transparent={tr} />
      <mesh position={[0, h * 0.70 + 0.28, 1.73]}>
        <boxGeometry args={[1.45, 0.32, 0.05]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.40} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, DOOR_H / 2 + 0.28, 1.74]}>
        <boxGeometry args={[DOOR_W, DOOR_H, 0.06]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, h + 0.92, 0]}>
        <sphereGeometry args={[0.36, 8, 8]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.48} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function FlatShape({ body, accent, dark, under, progress, animRef }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  return (
    <group>
      <mesh position={[0, 0.46, 0]}>
        <cylinderGeometry args={[0.34, 0.42, 0.40, 6, 1, false, HEX_THETA_START]} />
        <MatBody color={dark} transparent={tr} opacity={op} />
      </mesh>
      <mesh ref={animRef} position={[0, 0.82, 0]} castShadow receiveShadow>
        <boxGeometry args={[MODULE_W, 0.10, MODULE_D]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, 0.885, 0]}>
        <boxGeometry args={[MODULE_W * 0.88, 0.02, MODULE_D * 0.88]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.40} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function IndustrialShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  const bodyH = h * 0.72;
  return (
    <group>
      <HexPrism radius={1.78} height={bodyH} y={bodyH / 2 + 0.28} color={body} opacity={op} transparent={tr} castShadow />
      <HexPrism radius={1.74} height={0.18} y={bodyH + 0.38} color={dark} opacity={op} transparent={tr} />
      <mesh position={[0.86, h * 0.86 + 0.28, 0.46]}>
        <cylinderGeometry args={[0.28, 0.34, h * 0.42, 8]} />
        <MatBody color={dark} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, bodyH * 0.62 + 0.28, 1.79]}>
        <boxGeometry args={[1.60, 0.20, 0.05]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={0.32} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, DOOR_H / 2 + 0.28, 1.80]}>
        <boxGeometry args={[DOOR_W, DOOR_H, 0.06]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function OpenShape({ h, body, accent, panel, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  const postR = 1.42;
  return (
    <group>
      {[0, 1, 2, 3, 4, 5].map((edge) => {
        const a = edge * Math.PI / 3;
        return (
          <mesh key={edge} position={[Math.cos(a) * postR, h / 2 + 0.28, Math.sin(a) * postR]} castShadow>
            <boxGeometry args={[0.22, h, 0.22]} />
            <MatBody color={body} transparent={tr} opacity={op} />
          </mesh>
        );
      })}
      <HexPrism radius={1.64} height={0.14} y={h + 0.34} color={accent} emissive={accent} emissiveIntensity={0.28} opacity={op} transparent={tr} />
      <mesh position={[-0.72, 0.95, 0.76]}>
        <boxGeometry args={[1.10, 1.25, 1.10]} />
        <MatBody color={panel} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function CylindricalShape({ h, body, accent, dark, under, progress }) {
  const op = under ? 0.5 + progress * 0.5 : 1;
  const tr = under;
  const radius = 1.55;
  return (
    <group>
      <mesh position={[0, h * 0.46 + 0.28, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[radius, radius, h * 0.86, 16]} />
        <MatBody color={body} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[0, h * 0.90 + 0.28, 0]}>
        <sphereGeometry args={[radius, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <MatBody color={dark} transparent={tr} opacity={op} />
      </mesh>
      <mesh position={[radius + 0.02, h * 0.44 + 0.28, 0]}>
        <boxGeometry args={[0.04, h * 0.34, 0.05]} />
        <MatBody color={accent} emissive={accent} emissiveIntensity={1.2} transparent={tr} opacity={op} />
      </mesh>
    </group>
  );
}

function DefaultShape(props) {
  return <BoxShape {...props} />;
}

function PrimaryShape({ shape, ...props }) {
  if (shape === "boxy") return <BoxShape {...props} />;
  if (shape === "sealed") return <SealedShape {...props} />;
  if (shape === "heavy") return <HeavyShape {...props} />;
  if (shape === "mechanical") return <MechanicalShape {...props} />;
  if (shape === "slim") return <SlimShape {...props} />;
  if (shape === "clean") return <CleanShape {...props} />;
  if (shape === "flat") return <FlatShape {...props} />;
  if (shape === "industrial") return <IndustrialShape {...props} />;
  if (shape === "open") return <OpenShape {...props} />;
  if (shape === "cylindrical") return <CylindricalShape {...props} />;
  return <DefaultShape {...props} />;
}

function SelectionFootprint({ footprint }) {
  return (
    <group>
      {footprint.map((cell, index) => {
        const p = axialToWorld(cell.q, cell.r);
        return (
          <mesh key={`select-${cell.q},${cell.r},${index}`} position={[p.x, 0.33, p.z]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[HEX_SIZE * 0.82, HEX_SIZE * 0.96, 6, 1, 0, Math.PI * 2]} />
            <meshBasicMaterial color="#00fa9a" transparent opacity={0.62} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
        );
      })}
    </group>
  );
}

// ──────────────────────────────────────────────
// Main building mesh component
// ──────────────────────────────────────────────
export function HexBuildingMesh({ b, selected, showConnectors, overlay }) {
  const def = EM2C_BUILDINGS[b.type];
  const groupRef = useRef();
  const animRef = useRef();
  const kitModel = useMemo(() => {
    if (!def) return null;
    const kitId = KIT_BY_NAME.get(def.name);
    if (!kitId) return null;
    const canonical = (def.footprintHexes?.length ? def.footprintHexes : [{ q: 0, r: 0 }]).map(({ q, r }) => [q, r]);
    try {
      const model = bakeKitBuilding(createKitBuilding(kitId, canonical, HEX_SIZE));
      // Foundation deck finishes at y=0.3075. Kit plinth starts at y=0.
      // Translate the entire assembly onto the deck; never scale its parts.
      model.position.y = 0.315;
      return model;
    }
    catch (error) { console.warn("[EM2C] Building kit model failed", def.name, error); return null; }
  }, [def]);

  useFrame(({ clock }) => {
    if (!def || !groupRef.current) return;
    const t = clock.elapsedTime;
    if (kitModel) updateKitBuilding(kitModel, t);

    // Only functional sub-components animate. The building itself stays snapped.
    if (def.shape === "flat" && animRef.current) {
      animRef.current.rotation.x = -Math.PI / 8 + Math.sin(t * 0.3) * 0.05;
    }
    if (def.shape === "slim" && animRef.current) {
      animRef.current.rotation.y = t * 0.5;
    }
  });

  if (!def) return null;

  const center = axialToWorld(b.q, b.r);
  // rotateFootprint() rotates (q,r) clockwise by this amount. Three.js positive
  // Y rotation is the opposite visual direction in this x/z coordinate system.
  const rotationY = -(b.rotation || 0) * (Math.PI / 3);

  const h = Math.max(0.8, def.size?.[1] || 2.5);
  const footprint = def.footprintHexes?.length ? def.footprintHexes : [{ q: 0, r: 0 }];
  const under = b.progress < 1;
  const progress = b.progress || 0;

  const overlayColor = overlay === "power" ? "#ffc107"
    : overlay === "water" ? "#00e5ff"
    : overlay === "oxygen" ? "#7fffd4"
    : null;
  // Semantic category paint makes the whole colony readable from overhead.
  // Keep identical panel/edge hardware and fixed module scale across categories.
  const categoryPaint = getColonyCategoryColors(def.category);
  const body = overlayColor && !under ? overlayColor : categoryPaint.body;
  const accent = overlayColor && !under ? overlayColor : categoryPaint.accent;
  const panel = "#c8ccd4";
  const dark = "#2a2f38";
  const shape = def.shape || "boxy";
  const shapeProps = { h, body, accent, panel, dark, under, progress, animRef };

  return (
    <group ref={groupRef} position={[center.x, 0, center.z]} rotation={[0, rotationY, 0]}>
      <FootprintFoundations
        footprint={footprint}
        body={body}
        accent={accent}
        dark={dark}
        under={under}
        progress={progress}
      />

      {/* Fill only the seams between adjacent cells owned by this same building. */}
      <FootprintSeamFill
        footprint={footprint}
        body={body}
        under={under}
        progress={progress}
      />

      {/* Origin module keeps the building's clear functional identity. */}
      {kitModel ? <primitive object={kitModel} /> : (b.type === "command_center"
        ? <OneHexCommandHub under={under} progress={progress} />
        : <PrimaryShape shape={shape} {...shapeProps} />)}

      {/* Preserve native fallback modules if the kit has no matching model. */}
      {!kitModel && <FootprintAnnexes footprint={footprint} shape={shape} {...shapeProps} />}

      {selected && <SelectionFootprint footprint={footprint} />}

      {under && (
        <mesh position={[0, h + 1.35, 0]}>
          <boxGeometry args={[2 * progress, 0.12, 0.12]} />
          <meshBasicMaterial color="#00fa9a" />
        </mesh>
      )}
    </group>
  );
}

// Legacy aggregate renderer retained for any scene still importing it.
export function HexBuildings({ buildings, selectedId, overlay, showConnectors, onSelect }) {
  return (
    <group>
      {buildings.map((b) => (
        <group key={b.id} onClick={(e) => { e.stopPropagation(); onSelect(b.id); }}>
          <HexBuildingMesh
            b={b}
            selected={b.id === selectedId}
            overlay={overlay}
            showConnectors={showConnectors}
          />
        </group>
      ))}
    </group>
  );
}
